'use client';

import { c as _c } from "react/compiler-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

/**
 * Client-side copy of a category page's title, description and Open Graph,
 * rendered ONLY when the catch-all route's generateMetadata could not resolve
 * them: its lightweight getCategoryPageMetadata read is fail-soft and returns
 * null on an Alexandria error, while the full category payload the page
 * renders from is an independent request that may have succeeded. On that
 * request the server emitted a bare 'Joybird' title and nothing else, so this
 * is the sole emitter and the one-emitter rule from CON-8801 still holds.
 * CategoryAlgoliaLayout renders it from its own category data when the server
 * wrappers (CategoryPageWrapper, ProductAndCategoryWrapper) report the failed
 * read via `serverSeoResolved`, so a client refetch of the category data is
 * covered too; nothing else may render it.
 *
 * The Helmet mock sets document.title rather than rendering a <title>
 * element, and og:type matches the server's 'website'.
 */
const CategorySeoFallback = t0 => {
  const $ = _c(17);
  const {
    categoryData
  } = t0;
  let t1;
  if ($[0] !== categoryData) {
    t1 = categoryData ?? {};
    $[0] = categoryData;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    pageHtmlTitle: t2,
    pageMetaDescription: t3,
    pageName: t4
  } = t1;
  const pageHtmlTitle = t2 === undefined ? "" : t2;
  const pageMetaDescription = t3 === undefined ? "" : t3;
  const pageName = t4 === undefined ? "" : t4;
  if (!pageHtmlTitle) {
    return null;
  }
  let t5;
  if ($[2] !== pageHtmlTitle) {
    t5 = <title>{pageHtmlTitle}</title>;
    $[2] = pageHtmlTitle;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== pageMetaDescription) {
    t6 = pageMetaDescription && <meta name="description" content={pageMetaDescription} />;
    $[4] = pageMetaDescription;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== pageHtmlTitle || $[7] !== pageName) {
    t7 = (pageName || pageHtmlTitle) && <meta property="og:title" content={pageName || pageHtmlTitle} />;
    $[6] = pageHtmlTitle;
    $[7] = pageName;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== pageMetaDescription) {
    t8 = pageMetaDescription && <meta property="og:description" content={pageMetaDescription} />;
    $[9] = pageMetaDescription;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <meta property="og:type" content="website" />;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t10 = <Helmet>{t5}{t6}{t7}{t8}{t9}</Helmet>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
};
CategorySeoFallback.propTypes = {
  categoryData: PropTypes.shape({
    pageHtmlTitle: PropTypes.string,
    pageMetaDescription: PropTypes.string,
    pageName: PropTypes.string
  })
};
export default CategorySeoFallback;